/* eslint-disable max-lines */
import {
  fetchBatchScheduleWeek,
  updateBatchScheduleWeek
} from '@actions/batches';
import useBatchSkuDetails from '@api/hooks/batch/useBatchSkuDetails';
import { useSelectorToStore } from '@hooks/reduxHooks';
import { getIsLoading, getRecordedBatchData } from '@selectors/batches';
import { scrollConstants } from '@utils/constants';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import {
  getIsSubscribedToGoal,
  getIsSubscribedToLite
} from '@selectors/subscription';
import {
  DEFAULT_SUBJECT_VALUE,
  getHasStorePurchase,
  SESSION_TYPE_MAP
} from '../utils';
import RecordedBatchHeader from './RecordedBatchHeader';
import WeekListing from './WeekListing';

const RecordedBatchSchedule = ({ batchUID, batchGoalUID }) => {
  const dispatch = useDispatch();
  const [sessionType, setSessionType] = useState(SESSION_TYPE_MAP.ALL);
  const [selectedSubject, setSelectedSubject] = useState(DEFAULT_SUBJECT_VALUE);
  const [selectedWeek, setSelectedWeek] = useState(1);
  const recordedBatchData = useSelectorToStore(getRecordedBatchData);
  const isLoading = useSelectorToStore(getIsLoading);
  const weeksRef = useRef({});
  const weekContainerRef = useRef();
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const isSubscribed = useSelector(getIsSubscribedToGoal(batchGoalUID));
  const isLiteUser = useSelector(getIsSubscribedToLite(batchGoalUID));
  const { batchSkuDetails, mutate: refetchBatchSkuDetails } =
    useBatchSkuDetails(batchGoalUID, batchUID);

  useEffect(() => {
    if (batchUID && batchGoalUID) {
      refetchBatchSkuDetails();
    }
  }, [batchUID, batchGoalUID, refetchBatchSkuDetails, isLoggedIn]);

  const hasStorePurchase = getHasStorePurchase(batchUID, batchSkuDetails);

  const { results: weeklyDetails, filters } = recordedBatchData || {};

  const scrollToWeek = (weekRef) => {
    setTimeout(() => {
      const topOffset =
        weekRef.current.offsetTop +
        (selectedWeek
          ? weeksRef.current[selectedWeek].current.clientHeight -
            scrollConstants.scroll32
          : 0);

      weekContainerRef.current?.scrollTo({
        left: 0,
        top: topOffset,
        behavior: 'smooth'
      });
    }, 700);
  };

  const handleSessionTypeChange = (value) => {
    return setSessionType(value);
  };

  const handleSubjectChange = (value) => {
    return setSelectedSubject(value);
  };

  const handleWeekSelection = (value) => {
    setSelectedWeek(value === selectedWeek ? 0 : value);
  };

  const handleJumpToWeek = (weekNumber) => {
    if (!weeksRef.current[weekNumber]) return;
    scrollToWeek(weeksRef.current[weekNumber], weekNumber);
    setSelectedWeek(weekNumber === selectedWeek ? 0 : weekNumber);
  };

  const fetchInitialData = async () => {
    const response = await dispatch(
      fetchBatchScheduleWeek(batchUID, selectedWeek)
    );

    const lastViewedWeek = response?.result?.current_week_number;

    if (lastViewedWeek && lastViewedWeek !== 1) {
      handleJumpToWeek(lastViewedWeek);
    }
  };

  useEffect(() => {
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedWeek) return;
    const selectedWeekData = recordedBatchData?.results?.find(
      (week) => week.weekNumber === selectedWeek
    );

    if (
      !isLoading &&
      selectedWeekData &&
      !selectedWeekData.isLoaded &&
      (selectedWeekData.noOfClasses > 0 ||
        selectedWeekData.numberOfTests > 0) &&
      (!selectedWeekData?.items || selectedWeekData?.items?.length <= 0)
    ) {
      dispatch(updateBatchScheduleWeek(batchUID, selectedWeek));
    }
  }, [dispatch, selectedWeek, batchUID, recordedBatchData, isLoading]);

  const hasAccessToBatch = hasStorePurchase || (isSubscribed && !isLiteUser);

  return (
    <>
      <RecordedBatchHeader
        batchUID={batchUID}
        sessionType={sessionType}
        selectedSubject={selectedSubject}
        selectedWeek={selectedWeek}
        handleSessionTypeChange={handleSessionTypeChange}
        handleSubjectChange={handleSubjectChange}
        handleJumpToWeek={handleJumpToWeek}
        weekDetails={weeklyDetails || []}
        filterOptions={filters || []}
      />
      <WeekListing
        selectedWeek={selectedWeek}
        onWeekSelect={handleWeekSelection}
        weeklyDetails={weeklyDetails}
        sessionType={sessionType}
        selectedSubject={selectedSubject}
        weeksRef={weeksRef}
        weekContainerRef={weekContainerRef}
        hasAccessToBatch={hasAccessToBatch}
        batchGoalUID={batchGoalUID}
      />
    </>
  );
};

export default RecordedBatchSchedule;

RecordedBatchSchedule.propTypes = {
  batchUID: PropTypes.string.isRequired
};
